import { inject } from '@angular/core';
import { Routes, Router } from '@angular/router';
import { authGuard, adminGuard, ownerGuard } from './services/auth.guard';
import { roleGuard } from './guards/role.guard';
import { RoleService } from './services/role.service';

export const APP_ROUTES: Routes = [
  // ── Public / unauthenticated ────────────────────────────────────────────
  {
    path: 'login',
    loadComponent: () =>
      import('./components/login/login.component').then(m => m.LoginComponent),
  },
  {
    path: 'r/:organizationId/:donationId',
    loadComponent: () =>
      import('./comps/public-receipt/public-receipt.component').then(m => m.PublicReceiptComponent),
  },
  {
    path: 'd/:categoryId/:itemId',
    loadComponent: () =>
      import('./comps/item-donation/item-donation.component').then(m => m.ItemDonationComponent),
  },
  // Legacy route — redirect to short form
  {
    path: ':organizationId/donation/:categoryId/:itemId',
    redirectTo: 'd/:categoryId/:itemId',
  },
  {
    path: 'unsubscribe',
    loadComponent: () =>
      import('./comps/unsubscribe/unsubscribe.component').then(m => m.UnsubscribeComponent),
  },
  {
    path: 'pledge/:pledgeId',
    loadComponent: () =>
      import('./comps/public-pledge/public-pledge.component').then(m => m.PublicPledgeComponent),
  },

  // ── Authenticated shell ─────────────────────────────────────────────────
  {
    path: '',
    loadComponent: () =>
      import('./components/shell/shell.component').then(m => m.ShellComponent),
    canActivate: [authGuard],
    children: [
      // Default route — redirect based on role
      {
        path: '',
        pathMatch: 'full',
        canActivate: [() => {
          const roleService = inject(RoleService);
          const router = inject(Router);
          return router.createUrlTree([
            roleService.isCollector() ? '/record-donation' : '/dashboard',
          ]);
        }],
        // A component is required even though the guard always redirects.
        // Lazy-load dashboard as a fallback (it will never render).
        loadComponent: () =>
          import('./comps/analytics/analytics.component').then(m => m.AnalyticsComponent),
      },
      // ── All roles ─────────────────────────────────────────────────────
      {
        path: 'record-donation',
        loadComponent: () =>
          import('./comps/record-donation/record-donation.component').then(m => m.RecordDonationComponent),
      },
      // ── Banker+ ───────────────────────────────────────────────────────
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./comps/analytics/analytics.component').then(m => m.AnalyticsComponent),
        canActivate: [roleGuard('banker')],
      },
      {
        path: 'donations',
        loadComponent: () =>
          import('./comps/transactions/transactions.component').then(m => m.TransactionsComponent),
        // No role guard — collectors see own transactions, bankers+ see all
      },
      {
        path: 'subscriptions',
        loadComponent: () =>
          import('./comps/subscriptions/subscriptions.component').then(m => m.SubscriptionsComponent),
        canActivate: [roleGuard('banker')],
      },
      {
        path: 'pledges',
        loadComponent: () =>
          import('./comps/pledges/pledges.component').then(m => m.PledgesComponent),
        canActivate: [roleGuard('banker')],
      },
      {
        path: 'donors',
        loadComponent: () =>
          import('./comps/donors/donors.component').then(m => m.DonorsComponent),
        canActivate: [roleGuard('banker')],
      },
      {
        path: 'items',
        loadComponent: () =>
          import('./comps/org-categories/org-categories.component').then(m => m.OrgCategoriesComponent),
        canActivate: [roleGuard('banker')],
      },
      // ── Admin+ ────────────────────────────────────────────────────────
      {
        path: 'devices',
        loadComponent: () =>
          import('./comps/devices/devices.component').then(m => m.DevicesComponent),
        canActivate: [roleGuard('admin')],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./comps/org-profile/org-profile.component').then(m => m.OrgProfileComponent),
        canActivate: [roleGuard('admin')],
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./comps/org-users/org-users.component').then(m => m.OrgUsersComponent),
        canActivate: [roleGuard('admin')],
      },
      {
        path: 'integrations/quickbooks',
        loadComponent: () =>
          import('./comps/qb-settings/qb-settings.component').then(m => m.QbSettingsComponent),
        canActivate: [roleGuard('admin')],
      },
      // ── Owner only ────────────────────────────────────────────────────
      {
        path: 'billing',
        loadComponent: () =>
          import('./comps/billing/billing.component').then(m => m.BillingComponent),
        canActivate: [ownerGuard],
      },
    ],
  },

  // ── Legacy redirects ────────────────────────────────────────────────────
  { path: 'console', redirectTo: '', pathMatch: 'full' },
  { path: '**', redirectTo: '' },
];
